/**
 * Keeps the applied brand in step with the server — §11.5.
 *
 * The cache has already been applied by `bootstrapBranding()` before React
 * mounted (see `main.tsx`); this component's job is the part that needs the
 * network and the session:
 *
 *   * fetch the authoritative payload and re-apply **only if the version
 *     differs**, so a repeat visit does not repaint for nothing;
 *   * re-apply from `/auth/me` after login, which is what makes a shared-host
 *     tenant switch correct — the login response knows which tenant the user
 *     actually belongs to, and `?tenant=` on the URL might have said otherwise.
 */

import type { BrandingPayload } from '@rd/shared';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type ReactElement } from 'react';

import { apiRequest } from '../lib/apiClient.js';
import { reportProblem } from '../lib/report.js';

import { applyBranding } from './applyBranding.js';
import { readCachedBranding, slugFromLocation, writeCachedBranding } from './brandingCache.js';

interface BrandingContextValue {
  branding: BrandingPayload | null;
  /** Called by the auth layer with the payload `/auth/me` returned. */
  adoptBranding(payload: BrandingPayload): void;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used inside <BrandingProvider>');
  return ctx;
}

export function BrandingProvider({ children }: { children: ReactNode }): ReactElement {
  const host = window.location.host;
  const slug = slugFromLocation(window.location.search);

  const [branding, setBranding] = useState<BrandingPayload | null>(() =>
    readCachedBranding(host, slug),
  );
  // The version currently painted. Compared rather than the whole payload
  // because `version` is exactly what the server bumps on an edit (§11.1 step 6)
  // and a deep compare would repaint on a formatting change.
  const appliedVersion = useRef<number | null>(branding?.version ?? null);

  const adopt = useCallback(
    (payload: BrandingPayload) => {
      setBranding(payload);
      writeCachedBranding(host, slug, payload);
      if (appliedVersion.current !== payload.version) {
        applyBranding(payload);
        appliedVersion.current = payload.version;
      }
    },
    [host, slug],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const payload = await apiRequest<BrandingPayload>('/branding', {
          ...(slug ? { query: { slug } } : {}),
          /**
           * **Revalidate on every load** (QA defect D4-01).
           *
           * §11.4 puts `Cache-Control: public, max-age=60` on this route, so
           * without this the browser reuses its copy for a minute and never
           * asks. Measured against the production bundle, every reload inside
           * that window rendered exactly one version behind — which means a
           * tenant's rebrand does not reach their existing users, and
           * white-labelling is the reason this product is generic.
           *
           * `'no-cache'` revalidates rather than refetches: the route carries
           * an ETag, so the usual answer is a bodiless 304. The `max-age`
           * stays as specified for any shared cache in front of us.
           */
          cache: 'no-cache',
        });
        if (!cancelled) adopt(payload);
      } catch (err) {
        // Handled, not hidden. The neutral palette from index.html is already
        // painted and the app is usable, so this must not block anyone — but a
        // rebrand that silently fails to arrive is precisely the failure nobody
        // could diagnose.
        reportProblem('branding', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt, slug]);

  return (
    <BrandingContext.Provider value={{ branding, adoptBranding: adopt }}>
      {children}
    </BrandingContext.Provider>
  );
}
